// /admin/companies/:companyId — FleetSpoke super admin: one customer account (CR_20260913 §7.3).
// Details (name, timezone; the code never changes — S4), people (D6a: invite, promote / demote,
// deactivate; the database keeps one active manager — S5), devices allocated to the account.
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { type FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { LastActive } from '../../components/LastActive';
import { Loading, LoadError, Page } from '../../components/Page';
import { StartSupportDialog } from '../../components/StartSupportDialog';
import { SupportLog } from '../../components/SupportLog';
import {
  type CompanyMember,
  type CompanyOverview,
  useCompany,
  useCompanyMembers,
  useDevices,
  useSetCompanyActive,
  useDeleteMember,
  useSetMember,
  useSuperAdmins,
  useUpdateCompany,
} from '../../data/admin';
import { deviceTypeName } from '../../lib/devices';
import { dateTime, relativeTime } from '../../lib/time';
import { InviteManagerDialog } from './CompaniesPage';

const TABS = ['overview', 'details', 'people', 'devices', 'support'] as const;
type TabKey = (typeof TABS)[number];

/** Database error → words (S5 last manager, reactivating someone active elsewhere, bad timezone). */
function useAdminError() {
  const { t } = useTranslation();
  return (error: unknown): string | null => {
    if (!error) return null;
    const code = (error as { code?: string }).code;
    if (code === '23514') return t('admin.err_last_manager');
    if (code === '23505') return t('settings.users.err_member_elsewhere');
    if (code === '22023') return t('admin.err_timezone');
    if (code === '42501') return t('common.not_allowed');
    if (code === '55000') return t('admin.delete_member_active');
    return t('common.error_generic');
  };
}

export function AdminCompanyDetailPage() {
  const { t } = useTranslation();
  const { companyId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab: TabKey = (TABS as readonly string[]).includes(params.get('tab') ?? '')
    ? (params.get('tab') as TabKey)
    : 'overview';
  const company = useCompany(companyId);
  const setActive = useSetCompanyActive();
  const [supporting, setSupporting] = useState(false);

  const back = (
    <Link
      component={RouterLink}
      to="/admin/companies"
      underline="hover"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      <ArrowBackIcon fontSize="small" />
      {t('admin.back_companies')}
    </Link>
  );

  if (company.isLoading) return <Loading />;
  if (company.error)
    return (
      <Box sx={{ p: 3 }}>
        <LoadError error={company.error} />
      </Box>
    );
  const c = company.data;
  if (!c || c.kind !== 'customer')
    return (
      <Box sx={{ p: 3 }}>
        {back}
        <Alert severity="warning">{t('admin.company_not_found')}</Alert>
      </Box>
    );

  return (
    <Page
      back={back}
      title={
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {c.name}
          <Chip size="small" label={c.code} sx={{ fontFamily: 'monospace' }} />
        </Box>
      }
      actions={
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            {t('admin.col_active')}
          </Typography>
          <Switch
            checked={c.is_active}
            onChange={(e) => setActive.mutate({ id: c.company_id, active: e.target.checked })}
            slotProps={{ input: { 'aria-label': t('admin.col_active') } }}
          />
          <Tooltip title={c.is_active ? '' : t('support.open_disabled')}>
            <span>
              <Button
                variant="contained"
                startIcon={<SupportAgentIcon />}
                onClick={() => setSupporting(true)}
                disabled={!c.is_active}
              >
                {t('support.open_as')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      }
    >
      <Tabs
        value={tab}
        onChange={(_, v: TabKey) => setParams({ tab: v }, { replace: true })}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="overview" label={t('admin.tab_overview')} />
        <Tab value="details" label={t('admin.tab_details')} />
        <Tab value="people" label={t('admin.tab_people')} />
        <Tab value="devices" label={t('admin.tab_devices')} />
        <Tab value="support" label={t('support.log_title')} />
      </Tabs>
      {tab === 'overview' ? <OverviewTab company={c} /> : null}
      {tab === 'details' ? <DetailsTab key={c.company_id} company={c} /> : null}
      {tab === 'people' ? <PeopleTab company={c} /> : null}
      {tab === 'devices' ? <DevicesTab company={c} /> : null}
      {tab === 'support' ? (
        <SupportLog
          companyId={c.company_id}
          timeZone={c.timezone}
          intro={t('support.log_intro_admin')}
          linkRecords={false}
        />
      ) : null}
      {supporting ? <StartSupportDialog company={c} onClose={() => setSupporting(false)} /> : null}
    </Page>
  );
}

function OverviewTab({ company: c }: { company: CompanyOverview }) {
  const { t, i18n } = useTranslation();
  const stats: [string, string | number][] = [
    [t('admin.col_managers'), c.managers],
    [t('admin.col_members'), c.members],
    [t('admin.col_sites'), c.sites],
    [t('admin.col_assets'), c.assets],
    [t('admin.col_tags'), c.tags],
    [t('admin.col_gateways'), c.gateways ? `${c.gateways_online} / ${c.gateways}` : '—'],
  ];
  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
        }}
      >
        {stats.map(([label, value]) => (
          <Card key={label} variant="outlined">
            <CardContent>
              <Typography variant="h5" component="p" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {t('admin.timezone')}: {c.timezone} · {t('admin.created')}:{' '}
        {dateTime(c.created_at, i18n.language, c.timezone)}
      </Typography>
    </Stack>
  );
}

function allTimezones(current: string): string[] {
  const zones = Intl.supportedValuesOf('timeZone');
  const list = zones.includes(current) ? zones : [current, ...zones];
  // the region FleetSpoke sells in first
  return [
    ...list.filter((z) => z.startsWith('Asia/')),
    ...list.filter((z) => !z.startsWith('Asia/')),
  ];
}

function DetailsTab({ company: c }: { company: CompanyOverview }) {
  const { t } = useTranslation();
  const update = useUpdateCompany();
  const adminError = useAdminError();
  const [name, setName] = useState(c.name);
  const [timezone, setTimezone] = useState(c.timezone);
  const zones = useMemo(() => allTimezones(c.timezone), [c.timezone]);
  const dirty = name.trim() !== c.name || timezone !== c.timezone;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    update.mutate({ id: c.company_id, name, timezone });
  };
  return (
    <Box component="form" onSubmit={submit} sx={{ maxWidth: 560 }}>
      <Stack spacing={2}>
        <TextField
          label={t('admin.company_name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <TextField
          label={t('admin.company_code')}
          value={c.code}
          disabled
          helperText={t('admin.code_locked')}
        />
        <TextField
          select
          label={t('admin.timezone')}
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          slotProps={{
            select: { MenuProps: { slotProps: { paper: { sx: { maxHeight: 360 } } } } },
          }}
        >
          {zones.map((z) => (
            <MenuItem key={z} value={z}>
              {z}
            </MenuItem>
          ))}
        </TextField>
        {update.error ? <Alert severity="error">{adminError(update.error)}</Alert> : null}
        {update.isSuccess && !dirty ? <Alert severity="success">{t('common.saved')}</Alert> : null}
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={!dirty || !name.trim() || update.isPending}
          >
            {t('common.save')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

function PeopleTab({ company: c }: { company: CompanyOverview }) {
  const { t, i18n } = useTranslation();
  const members = useCompanyMembers(c.company_id);
  // a super admin keeps only deactivated membership rows here (history): shown as such, with nothing to act on —
  // the database refuses to reactivate them (20260925140000)
  const superAdmins = useSuperAdmins();
  const isSuper = (m: CompanyMember) => !!superAdmins.data?.some((a) => a.user_id === m.user_id);
  const setMember = useSetMember();
  const deleteMember = useDeleteMember();
  const [deleting, setDeleting] = useState<CompanyMember | null>(null);
  const adminError = useAdminError();
  const [inviting, setInviting] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; member: CompanyMember } | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; text: string } | null>(
    null,
  );

  const change = (m: CompanyMember, input: { role?: 'manager'; active?: boolean }) => {
    setMenu(null);
    setNotice(null);
    setMember.mutate(
      { membershipId: m.membership_id, ...input },
      {
        onSuccess: () => setNotice({ severity: 'success', text: t('admin.member_updated') }),
        onError: (e) => setNotice({ severity: 'error', text: adminError(e) ?? '' }),
      },
    );
  };
  const stateLabel = (m: CompanyMember) =>
    m.invite_state === 'invited'
      ? t('admin.state_invited')
      : m.invite_state === 'deactivated'
        ? t('settings.users.status_inactive')
        : t('settings.users.status_active');

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
      >
        <Typography variant="body2" color="text.secondary">
          {t('admin.users_by_managers')}
        </Typography>
        <Button variant="contained" onClick={() => setInviting(true)} disabled={!c.is_active}>
          {t('admin.invite_manager')}
        </Button>
      </Stack>
      {notice ? (
        <Alert severity={notice.severity} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      ) : null}
      {members.isLoading ? <Loading /> : null}
      {members.error ? (
        <LoadError error={members.error} onRetry={() => void members.refetch()} />
      ) : null}
      {members.data && members.data.length === 0 ? (
        <Typography color="text.secondary">{t('admin.people_empty')}</Typography>
      ) : null}
      {members.data && members.data.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.users.name')}</TableCell>
                <TableCell>{t('settings.users.phone')}</TableCell>
                <TableCell>{t('settings.users.email')}</TableCell>
                <TableCell>{t('settings.users.role')}</TableCell>
                <TableCell>{t('admin.col_status')}</TableCell>
                <TableCell>{t('settings.users.last_active')}</TableCell>
                <TableCell>{t('admin.col_invited')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {members.data.map((m) => (
                <TableRow key={m.membership_id} sx={{ opacity: m.is_active ? 1 : 0.6 }}>
                  <TableCell>{m.full_name ?? '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{m.phone ?? '—'}</TableCell>
                  <TableCell>{m.email ?? '—'}</TableCell>
                  <TableCell>
                    {isSuper(m) ? (
                      <Tooltip title={t('admin.super.chip_help')}>
                        <Chip
                          size="small"
                          color="secondary"
                          variant="outlined"
                          label={t('admin.super.chip')}
                          data-testid="super-admin-chip"
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        color={m.role === 'manager' ? 'primary' : 'default'}
                        variant={m.role === 'manager' ? 'filled' : 'outlined'}
                        label={t(`settings.users.role_${m.role}`)}
                      />
                    )}
                  </TableCell>
                  <TableCell>{stateLabel(m)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <LastActive
                      activeAt={m.last_active_at}
                      signedInAt={m.last_sign_in_at}
                      timeZone={c.timezone}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {dateTime(m.invited_at, i18n.language, c.timezone)}
                    {m.invited_by_name ? ` · ${m.invited_by_name}` : ''}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`${t('admin.member_actions')} ${m.full_name ?? m.phone ?? ''}`}
                      onClick={(e) => setMenu({ anchor: e.currentTarget, member: m })}
                      disabled={setMember.isPending || deleteMember.isPending}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {/* a promotion only: moving a manager to staff needs their reach chosen, so it is done in Settings → Users
            ("Open as this company") through set_member_access */}
        {menu && menu.member.is_active && menu.member.role !== 'manager' ? (
          <MenuItem onClick={() => change(menu.member, { role: 'manager' })}>
            {t('admin.make_manager')}
          </MenuItem>
        ) : null}
        {/* a super admin's old row cannot come back (20260925140000): it can only be deleted */}
        {menu && !isSuper(menu.member) ? (
          <MenuItem onClick={() => change(menu.member, { active: !menu.member.is_active })}>
            {menu.member.is_active
              ? t('settings.users.deactivate')
              : t('settings.users.reactivate')}
          </MenuItem>
        ) : null}
        {/* deactivated rows only: the row and the person's grants here go, their work and account stay */}
        {menu && !menu.member.is_active ? (
          <MenuItem
            onClick={() => {
              setDeleting(menu.member);
              setMenu(null);
            }}
            sx={{ color: 'error.main' }}
          >
            {t('admin.delete_member')}
          </MenuItem>
        ) : null}
      </Menu>
      {deleting ? (
        <Dialog open onClose={() => setDeleting(null)} fullWidth maxWidth="xs">
          <DialogTitle>
            {t('admin.delete_member_title', {
              name: deleting.full_name ?? deleting.phone ?? '—',
              company: c.name,
            })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{t('admin.delete_member_body')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleting(null)}>{t('common.cancel')}</Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteMember.isPending}
              data-testid="delete-member-confirm"
              onClick={() =>
                deleteMember.mutate(deleting.membership_id, {
                  onSuccess: () =>
                    setNotice({ severity: 'success', text: t('admin.member_deleted') }),
                  onError: (e) => setNotice({ severity: 'error', text: adminError(e) ?? '' }),
                  onSettled: () => setDeleting(null),
                })
              }
            >
              {t('admin.delete_member')}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
      {inviting ? <InviteManagerDialog company={c} onClose={() => setInviting(false)} /> : null}
    </Stack>
  );
}

function DevicesTab({ company: c }: { company: CompanyOverview }) {
  const { t, i18n } = useTranslation();
  const devices = useDevices();
  const mine = (devices.data ?? []).filter((d) => d.company_id === c.company_id);
  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
        <Button
          component={RouterLink}
          to={
            c.kind === 'platform'
              ? '/admin/devices?tab=available'
              : `/admin/devices?tab=assigned&account=${encodeURIComponent(c.code)}`
          }
          variant="outlined"
        >
          {t('admin.manage_devices')}
        </Button>
        {devices.data ? (
          <Typography variant="body2" color="text.secondary" data-testid="device-count">
            {t('admin.devices_total', { count: mine.length })}
          </Typography>
        ) : null}
      </Stack>
      {devices.isLoading ? <Loading /> : null}
      {devices.error ? <LoadError error={devices.error} /> : null}
      {devices.data && mine.length === 0 ? (
        <Typography color="text.secondary">{t('admin.no_devices')}</Typography>
      ) : null}
      {mine.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.device_type')}</TableCell>
                <TableCell>{t('admin.identifier')}</TableCell>
                <TableCell>{t('admin.col_status')}</TableCell>
                <TableCell>{t('admin.col_attached')}</TableCell>
                <TableCell>{t('admin.col_last_seen')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mine.map((d) => (
                <TableRow key={`${d.kind}-${d.id}`}>
                  <TableCell>
                    {d.device_type ? deviceTypeName(d.device_type) : t(`admin.type_${d.type}`)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{d.identifier}</TableCell>
                  <TableCell>{t(`admin.status_${d.status}`)}</TableCell>
                  <TableCell>{d.attached ?? '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {relativeTime(d.last_seen_at, i18n.language) ?? t('common.never')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </Stack>
  );
}
